import React from 'react';
import { useSelector } from 'react-redux';
import { LayoutContainer, LoaderContainer } from './style';
import Header from './Header';
import FilterBar from './FilterBar';
import MainContent from './MainContent';
import MainContentCms from './MainContentCms';
import Loader from '../../components/Loader/Loader';
import store from '../../store/store';
import {
  selectHasCmsLayout,
  selectCmsLoading,
  selectCmsCurrentProductId,
  selectCmsProductLayouts,
} from '../../store/slices/cmsSlice';
import { isCmsEnabled } from '../../services/cms/datoCmsClient';
import { selectFilterValue } from '../../store/slices/filterSliceV2';
import { cmsLayoutExclusions } from '../../constants/Constants';

interface LayoutV2Props {
  tabArray?: Array<{ tabName?: string; [key: string]: unknown }>;
  product?: {
    productId?: number;
    productName?: string;
    [key: string]: unknown;
  };
  model?: { id?: number; [key: string]: unknown };
  children?: React.ReactNode;
}

const LayoutV2: React.FC<LayoutV2Props> = ({ tabArray, product, model }) => {
  const isMFE = store.getState().app.isMFE;

  // Check if CMS layout is loading or available
  const cmsLoading = useSelector(selectCmsLoading);
  const hasCmsLayout = useSelector(selectHasCmsLayout);
  const cmsCurrentProductId = useSelector(selectCmsCurrentProductId);
  const productLayouts = useSelector(selectCmsProductLayouts);
  const cmsEnabled = isCmsEnabled();

  // Get selected program ID to check for exclusions
  const selectedProgramId = useSelector(
    (state: ReturnType<typeof store.getState>) =>
      selectFilterValue(state, 'program')
  );

  // Get the actual product ID from the prop
  const currentProductId = product?.productId;

  // Check if program or product is excluded from CMS layout checks
  const isExcludedFromCms =
    (selectedProgramId != null &&
      cmsLayoutExclusions.programIds.includes(selectedProgramId as number)) ||
    (currentProductId != null &&
      cmsLayoutExclusions.productIds.includes(currentProductId));

  // Check if this specific product's CMS status is known (cached)
  const isCurrentProductCached =
    currentProductId != null && currentProductId in productLayouts;

  // Determine if we should wait for CMS check
  // Wait if: CMS is enabled AND not excluded AND (product mismatch OR product not cached) AND not already loading
  const productMismatch =
    currentProductId != null && cmsCurrentProductId !== currentProductId;
  const shouldWaitForCmsCheck =
    cmsEnabled &&
    !isExcludedFromCms &&
    !cmsLoading &&
    (productMismatch || !isCurrentProductCached);

  // Show loader while CMS is fetching OR while waiting for CMS check to start
  // Skip loader for excluded programs/products - they always use default rendering
  // This prevents rendering MainContent prematurely before we know if CMS has a layout
  if (!isExcludedFromCms && (cmsLoading || shouldWaitForCmsCheck)) {
    return (
      <LayoutContainer>
        {!isMFE && <Header />}
        <FilterBar />
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      {!isMFE && <Header />}
      <FilterBar />
      {hasCmsLayout && !isExcludedFromCms ? (
        <MainContentCms product={product} />
      ) : (
        <MainContent tabArray={tabArray} product={product} model={model} />
      )}
    </LayoutContainer>
  );
};

export default LayoutV2;
